import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { graphqlRequest } from '@/api/amplify/helpers';
import { appsByAppKey } from '@/graphql/queries';
import { IntegratedApp } from '@/constants/apps';
import { useCurrentApp } from '@/stores/userDataStore';
import { app } from '@/types/entities';

const THREE_SECONDS_IN_MS = 3000;

const getHasCreatedSearchProducts = (app: app) => {
  const appData = JSON.parse(app?.data || '{}');
  return appData.hasCreatedElasticSearchProducts;
};

/**
 * Hook to check if Elasticsearch products have been created for the current app.
 *
 * Elasticsearch products are created during the app's installation process.
 * When the user first signs in, we need to refetch this information to ensure
 * we have the most up-to-date status of Elasticsearch product creation.
 *
 * @returns {boolean | null} Returns true if Elasticsearch products have been created,
 *                           false if not, or null if the data is still loading.
 */
export const useHasCreatedSearchProducts = (): {
  hasCreatedSearchProducts: boolean | null;
  isLoading: boolean;
} => {
  const { appKey, appUrl, isLoggedIn, isLoading: isLoadingUser } = useCurrentApp();

  const { data: apps, isLoading } = useQuery({
    queryKey: ['use-has-created-search-products', appKey],
    queryFn: async (): Promise<app[]> => {
      const {
        data: {
          appsByAppKey: { items: apps },
        },
      } = await graphqlRequest({
        query: appsByAppKey,
        variables: {
          appKey,
          limit: 500,
          filter: {
            or: [{ app: { eq: IntegratedApp.SHOPIFY } }, { app: { eq: IntegratedApp.STUDIO_SHOPIFY } }],
          },
        },
      });

      return apps;
    },
    staleTime: Infinity,
    refetchInterval: ({ state }) => {
      const { data } = state;

      if (!data) {
        return THREE_SECONDS_IN_MS;
      }

      const app = data.find((app) => app.appUrl === appUrl);
      if (!app) {
        return THREE_SECONDS_IN_MS;
      }

      const hasCreatedSearchProducts = getHasCreatedSearchProducts(app);
      if (hasCreatedSearchProducts) {
        return false;
      }

      return THREE_SECONDS_IN_MS;
    },
    enabled: !!appKey && isLoggedIn,
  });

  const hasCreatedSearchProducts = useMemo(() => {
    if (isLoading) {
      return null;
    }

    const app = apps?.find((app) => app.active && app.appUrl === appUrl);
    if (!app) {
      return null;
    }

    return getHasCreatedSearchProducts(app);
  }, [apps, isLoading, appUrl]);

  return { hasCreatedSearchProducts, isLoading: isLoadingUser || isLoading };
};
